<template>
    <div class="step-params">
        <ConfirmModal />
        <h3>2. Paramètres du tournoi</h3>

        <div class="form-group">
            <label>Saison</label>
            <select v-model.number="params.seasonId" class="form-input">
                <option :value="null" disabled>-- choisir --</option>
                <option v-for="s in seasons" :value="s.id" :key="s.id">
                    {{ s.label }}{{ s.is_current ? ' (en cours)' : '' }}
                </option>
            </select>
        </div>

        <div class="form-group">
            <label>Tournoi</label>
            <EventPicker
                v-model="params.eventId"
                :scopeFilter="eventScopeFilter"
                :seasonLabel="selectedSeasonLabel"
                @select="onEventSelect"
            />
            <p class="hint">Obligatoire -- l'import doit être lié à un tournoi interne existant.</p>
        </div>

        <template v-if="params.eventId">
            <p v-if="!lockedScope" class="detected-hint">
                Périmètre et type de points détectés depuis le tournoi -- à corriger ci-dessous si besoin.
            </p>

            <div v-if="!lockedScope" class="form-group">
                <label>Périmètre</label>
                <select v-model="params.scopeLevel" class="form-input" @change="params.scopeId = null">
                    <option :value="null" disabled>-- choisir --</option>
                    <option value="comite">Comité</option>
                    <option value="ligue">Ligue</option>
                </select>
            </div>

            <div v-if="!lockedScope && params.scopeLevel === 'comite'" class="form-group">
                <label>Comité</label>
                <select v-model.number="params.scopeId" class="form-input">
                    <option :value="null" disabled>-- choisir --</option>
                    <option v-for="c in comites" :value="c.id" :key="c.id">{{ c.name }}</option>
                </select>
            </div>

            <div v-if="!lockedScope && params.scopeLevel === 'ligue'" class="form-group">
                <label>Ligue</label>
                <select v-model.number="params.scopeId" class="form-input">
                    <option :value="null" disabled>-- choisir --</option>
                    <option v-for="l in ligues" :value="l.id" :key="l.id">{{ l.name }}</option>
                </select>
            </div>

            <div v-if="params.scopeLevel" class="form-group">
                <label>Type de points</label>
                <select v-model="params.sourceTypeCode" class="form-input" :disabled="!sourceTypes.length">
                    <option :value="null" disabled>-- choisir --</option>
                    <option v-for="t in sourceTypes" :value="t.code" :key="t.code">{{ t.label }} ({{ t.code }})</option>
                </select>
                <p v-if="params.scopeLevel && params.seasonId && !sourceTypes.length" class="hint">
                    Aucun type de points disponible pour cette saison/ce périmètre.
                </p>
            </div>

            <p v-if="existingImport" class="existing-import-banner">
                ⚠️ Ce tournoi a déjà été importé ({{ existingImport.rows_count }} ligne(s), le
                {{ formatImportDate(existingImport.imported_at) }}{{ existingImport.imported_by_email ? ` par ${existingImport.imported_by_email}` : '' }}).
                Refaire l'import créerait un doublon (points comptés deux fois) -- supprimez d'abord l'ancien import.
                Pour ajouter une catégorie oubliée, supprimez-le puis refaites l'import avec <strong>toutes</strong>
                les catégories (tous les fichiers en une fois).
                <button type="button" class="btn-secondary" :disabled="deletingExisting" @click="onDeleteExisting">
                    {{ deletingExisting ? 'Suppression...' : "Supprimer l'import existant" }}
                </button>
            </p>
        </template>

        <div class="form-actions">
            <button type="button" class="btn-secondary" @click="$emit('back')">← Retour</button>
            <button type="button" class="btn-submit" :disabled="!isValid || !!existingImport" @click="$emit('next')">Continuer</button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import dartsHubApi from '@/axios/dartsHubApi'
import { handleError } from '@/functions/utils'
import { useConfirm } from '@/composables/useConfirm'
import ConfirmModal from '@/components/ConfirmModal.vue'
import EventPicker from './EventPicker.vue'
import {
    guessScopeAndSourceType,
    type WizardParams, type SeasonOption, type ComiteOption, type LigueOption,
    type SourceTypeOption, type EventOption, type LockedScope,
} from './types'

const { confirm } = useConfirm()

const props = defineProps<{ params: WizardParams; lockedScope: LockedScope | null }>()
defineEmits<{ next: []; back: [] }>()

// Restreint EventPicker au calendrier du comité/de la ligue de l'admin connecté -- "leur
// propre calendrier", cf. discussion du 2026-09-20. `null` (super_admin) = pas de filtre.
const eventScopeFilter = computed(() =>
    props.lockedScope ? { level: props.lockedScope.level, code: props.lockedScope.code } : null
)
// Un tournoi d'une AUTRE saison pouvait apparaître dans la recherche alors que la saison
// choisie juste au-dessus ne correspond pas (bug constaté le 2026-09-20, EventPicker ne
// filtrait pas du tout par saison) -- on lui transmet le label de la saison sélectionnée.
const selectedSeasonLabel = computed(() => seasons.value.find((s) => s.id === props.params.seasonId)?.label ?? null)

const seasons = ref<SeasonOption[]>([])
const comites = ref<ComiteOption[]>([])
const ligues = ref<LigueOption[]>([])
const sourceTypes = ref<SourceTypeOption[]>([])
// Type de points deviné depuis le nom du tournoi, appliqué dès que la liste réelle des
// types dispo pour ce périmètre/cette saison arrive (fetchSourceTypes est asynchrone et
// réinitialise sourceTypeCode à chaque appel -- cf. plus bas).
const pendingSourceTypeGuess = ref<string | null>(null)

function applyLockedScope() {
    if (!props.lockedScope) return
    props.params.scopeLevel = props.lockedScope.level
    props.params.scopeId = props.lockedScope.id
}

function onEventSelect(event: EventOption | null) {
    // Le tournoi choisi porte déjà sa date, son nom et son lieu -- l'admin n'a jamais à les
    // ressaisir (cf. consigne utilisateur : "sélectionner une saison, un tournoi, c'est
    // tout"). Pour un rep comité/ligue, le périmètre est VERROUILLÉ sur sa propre entité
    // (cf. lockedScope) -- seul le type de points reste à deviner. Pour le super_admin
    // (lockedScope=null), périmètre ET type de points sont une PRÉ-SUGGESTION devinée
    // depuis le nom du tournoi, visible et modifiable, jamais masquée.
    props.params.tournamentDate = event?.date ?? null
    props.params.name = event?.display_name || event?.name || ''
    props.params.place = event?.place ?? null

    if (props.lockedScope) {
        applyLockedScope()
        pendingSourceTypeGuess.value = event ? guessScopeAndSourceType(event.name)?.sourceTypeCode ?? null : null
        return
    }

    if (!event) {
        props.params.scopeLevel = null
        props.params.scopeId = null
        pendingSourceTypeGuess.value = null
        return
    }

    const guess = guessScopeAndSourceType(event.name)
    if (guess) {
        props.params.scopeLevel = guess.scopeLevel
        const code = guess.scopeLevel === 'comite' ? event.comite : event.ligue
        const list = guess.scopeLevel === 'comite' ? comites.value : ligues.value
        props.params.scopeId = list.find((o) => o.code === code)?.id ?? null
        pendingSourceTypeGuess.value = guess.sourceTypeCode
    } else {
        props.params.scopeLevel = null
        props.params.scopeId = null
        pendingSourceTypeGuess.value = null
    }
}

onMounted(async () => {
    applyLockedScope()
    try {
        const [seasonsRes, comitesRes, liguesRes] = await Promise.all([
            dartsHubApi.get('/seasons'),
            dartsHubApi.get('/clubs/comites'),
            dartsHubApi.get('/clubs/ligues'),
        ])
        seasons.value = seasonsRes.data
        comites.value = comitesRes.data
        ligues.value = liguesRes.data
        if (!props.params.seasonId) {
            const current = seasons.value.find((s) => s.is_current)
            if (current) props.params.seasonId = current.id
        }
    } catch (err) {
        handleError(err, 'chargement des paramètres d\'import')
    }
})

async function fetchSourceTypes() {
    sourceTypes.value = []
    props.params.sourceTypeCode = null
    if (!props.params.scopeLevel || !props.params.seasonId) return
    try {
        const { data } = await dartsHubApi.get('/leaderboard/source-types', {
            params: { level: props.params.scopeLevel, season_id: props.params.seasonId },
        })
        sourceTypes.value = data
        if (pendingSourceTypeGuess.value && data.some((t: SourceTypeOption) => t.code === pendingSourceTypeGuess.value)) {
            props.params.sourceTypeCode = pendingSourceTypeGuess.value
        }
        pendingSourceTypeGuess.value = null
    } catch (err) {
        handleError(err, 'chargement des types de points')
    }
}

watch(() => [props.params.scopeLevel, props.params.seasonId], fetchSourceTypes)

interface ExistingImport {
    ranking_tournament_id: number
    rows_count: number
    imported_at: string | null
    imported_by_email: string | null
}

// Un import "manuel"/DartConnect ne remplace jamais un import existant pour le même
// tournoi -- il en recrée un second à côté, ce qui double les points de tout le monde
// dans le classement de saison (cf. /leaderboard/import/existing côté backend). On
// bloque donc "Continuer" tant qu'un import précédent existe pour cette combinaison
// événement/type de points/saison, avec un bouton pour le supprimer d'abord.
const existingImport = ref<ExistingImport | null>(null)
const deletingExisting = ref(false)

async function checkExistingImport() {
    existingImport.value = null
    const p = props.params
    if (!p.eventId || !p.sourceTypeCode || !p.seasonId) return
    try {
        const { data } = await dartsHubApi.get('/leaderboard/import/existing', {
            params: { event_id: p.eventId, source_type_code: p.sourceTypeCode, season_id: p.seasonId },
        })
        existingImport.value = data.found ? data : null
    } catch (err) {
        handleError(err, 'vérification d\'un import existant')
    }
}

watch(() => [props.params.eventId, props.params.sourceTypeCode, props.params.seasonId], checkExistingImport)

function formatImportDate(iso: string | null): string {
    if (!iso) return 'date inconnue'
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
}

async function onDeleteExisting() {
    if (!existingImport.value) return
    const accepted = await confirm(
        `Supprimer l'import existant (${existingImport.value.rows_count} ligne(s)) ? ` +
        'Cette action est définitive -- les points déjà comptés dans le classement de saison seront retirés.'
    )
    if (!accepted) return

    deletingExisting.value = true
    try {
        await dartsHubApi.delete(`/leaderboard/ranking-tournament/${existingImport.value.ranking_tournament_id}`)
        toast.success('Import existant supprimé.')
        existingImport.value = null
    } catch (err) {
        handleError(err, 'suppression de l\'import existant')
    } finally {
        deletingExisting.value = false
    }
}

const isValid = computed(() => {
    const p = props.params
    if (!p.seasonId || !p.scopeLevel || !p.sourceTypeCode || !p.scopeId) return false
    if (!p.eventId || !p.name.trim()) return false
    return true
})
</script>

<style scoped>
.step-params h3 {
    margin-bottom: 1rem;
}

.form-group {
    margin-bottom: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    max-width: 420px;
}

.form-group label {
    font-weight: 600;
    font-size: 0.9rem;
}

.hint {
    font-size: 0.85rem;
    color: var(--color-fg-muted, #888);
    margin: 0.15rem 0 0;
}

.detected-hint {
    font-size: 0.85rem;
    color: var(--color-fg-muted, #888);
    margin: 0.25rem 0 1rem;
    font-style: italic;
}

.existing-import-banner {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    font-size: 0.9rem;
    color: var(--color-danger, #d9534f);
    font-weight: 600;
    margin: 1rem 0;
    max-width: 640px;
}

.form-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.5rem;
}

.btn-submit,
.btn-secondary {
    display: inline-flex;
    align-items: center;
    padding: 0.6rem 1.2rem;
    border-radius: var(--radius, 8px);
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--color-accent, #1a3a5c);
}

.btn-submit {
    background: var(--color-accent, #1a3a5c);
    color: #fff;
}

.btn-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-secondary {
    background: transparent;
    color: var(--color-accent, #1a3a5c);
}
</style>
